import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import { api } from '../../../api';
import statsData from '../../../siteContentData.json';

interface StatItem {
  _id: string;
  id: string;
  number: string;
  label: string;
  __v: number;
}

const AdminStats: React.FC = () => {
  const [stats, setStats] = useState<StatItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Chargement initial des données
    const loadStats = async () => {
      try {
        setIsLoading(true);
        // Dans une vraie application, vous feriez une requête API ici
        // const response = await api.get('/admin/customization/stats');
        // setStats(response.data);
        
        // Pour cet exemple, on utilise les données locales
        setStats(statsData.stats);
      } catch (error) {
        console.error('Error fetching stats:', error);
        toast.error('Failed to load stats.');
      } finally {
        setIsLoading(false);
      }
    };
    loadStats();
  }, []);

  const handleStatChange = (index: number, field: keyof StatItem, value: string) => {
    const updatedStats = [...stats];
    updatedStats[index] = {
      ...updatedStats[index],
      [field]: value
    };
    setStats(updatedStats);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Dans une vraie application, vous feriez une requête API ici
      // await api.put('/admin/customization/stats', { stats });
      // Pour cet exemple, on simule juste la sauvegarde
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Stats updated successfully!');
    } catch (error) {
      console.error('Error updating stats:', error);
      toast.error('Failed to update stats.');
    } finally {
      setIsSaving(false);
    }
  };

  const addNewStat = () => {
    setStats([
      ...stats,
      {
        _id: `new-${Date.now()}`,
        id: `stat${stats.length + 1}`,
        number: '',
        label: '',
        __v: 0
      }
    ]);
  };

  const removeStat = (index: number) => {
    const updatedStats = [...stats];
    updatedStats.splice(index, 1);
    setStats(updatedStats);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Manage Statistics</h1>
        
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Statistics Items</h2>
              <button
                type="button"
                onClick={addNewStat}
                className="px-3 py-1 bg-[#FFB639]/100 text-white rounded hover:bg-[#FFB639] text-sm"
              >
                Add New Stat
              </button>
            </div>

            <div className="space-y-4">
              {stats.map((stat, index) => (
                <div key={stat._id} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end p-4 border rounded-lg">
                  <div>
                    <label htmlFor={`number-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Number/Value
                    </label>
                    <input
                      id={`number-${index}`}
                      type="text"
                      value={stat.number}
                      onChange={(e) => handleStatChange(index, 'number', e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                      placeholder="400+"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor={`label-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Label
                    </label>
                    <input
                      id={`label-${index}`}
                      type="text"
                      value={stat.label}
                      onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                      placeholder="Clubs partenaires"
                    />
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeStat(index)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#FFB639] hover:bg-[#e5a333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFB639] disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AdminStats;